// Docs: IMPLEMENTATION.md §Frontend Overlays (global LRO chip/tray).
// Tracks every background operation announced via the `lro:start` window
// event, plus client-driven progress announced via `lro:local` (bulk actions
// and exports the frontend performs itself, with no backend operation id).
// Every LRO renders in one small square chip with the aggregate progress;
// tapping it opens a panel listing each operation with details, dismissal,
// and failure info. The chip can be dragged (or flicked) anywhere and snaps
// to the nearest of six dock points; arrow keys move it while focused.
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { operationsAPI } from '../services/api';
import OperationFailureDialog from './OperationFailureDialog';
import {
  DOCKS,
  dockParts,
  dockPosition,
  loadDock,
  nearestDock,
  projectFling,
  saveDock,
  stepDock,
} from '../utils/trayDock';

const POLL_MS = 1500;
// Keep a cleanly finished operation visible briefly before it leaves the tray.
const SUCCESS_LINGER_MS = 2500;

const TERMINAL_STATUSES = ['succeeded', 'failed', 'cancelled'];
const isTerminal = (op) => TERMINAL_STATUSES.includes(op?.status);
const hasFailures = (op) => op?.status === 'failed' || (op?.failed_tasks || 0) > 0;
// Local (client-driven) ops carry status 'active' | 'succeeded' | 'failed'.
const isLocalTerminal = (op) => op?.status === 'succeeded' || op?.status === 'failed';

// Backend operation names → user-facing German titles.
const OPERATION_TITLES = {
  words_import: 'Wörter importieren',
  words_reclassify: 'Wörter aktualisieren',
  'Merge duplicate words': 'Duplikate zusammenführen',
  'Regenerate audio for words': 'Audio wird generiert',
};

const operationTitle = (op) => {
  if (!op || !op.name) return 'Vorgang';
  if (op.name.startsWith('Generate podcast')) return 'Podcast wird erstellt';
  return OPERATION_TITLES[op.name] || op.name;
};

// Positioning (relative/absolute) is up to the caller via className.
const SegmentedBar = ({ success, failed, total, className = '' }) => {
  const successPct = total > 0 ? Math.min(100, Math.round((success / total) * 100)) : 0;
  const failedPct = total > 0 ? Math.min(100 - successPct, Math.round((failed / total) * 100)) : 0;
  return (
    <div className={`w-full bg-[var(--cream)] overflow-hidden ${className}`}>
      <div
        className="absolute left-0 top-0 h-full bg-[var(--success)] transition-all"
        style={{ width: `${successPct}%` }}
      />
      <div
        className="absolute top-0 h-full bg-[var(--error)] transition-all"
        style={{ left: `${successPct}%`, width: `${failedPct}%` }}
      />
    </div>
  );
};

/**
 * One tracked operation. Owns its own polling loop so progress keeps updating
 * while the panel is closed (the row renders nothing then, but stays
 * mounted). Reports every snapshot upward for the chip's aggregate progress.
 */
const OperationRow = ({ operationId, hidden, onSnapshot, onDone, onShowFailure }) => {
  const [op, setOp] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let timer = null;
    let lingerTimer = null;

    const fetchOp = async () => {
      try {
        const data = await operationsAPI.get(operationId);
        if (cancelled) return;
        setOp(data);
        onSnapshot(operationId, data);
        if (isTerminal(data)) {
          if (!hasFailures(data)) {
            lingerTimer = setTimeout(() => {
              if (!cancelled) onDone(operationId, data);
            }, SUCCESS_LINGER_MS);
          }
          // Failed operations stay in the tray until dismissed, so the
          // failure isn't lost while the user looks elsewhere.
        } else {
          timer = setTimeout(fetchOp, POLL_MS);
        }
      } catch (e) {
        // Swallow errors; try again later.
        if (!cancelled) timer = setTimeout(fetchOp, POLL_MS * 2);
      }
    };

    fetchOp();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      if (lingerTimer) clearTimeout(lingerTimer);
    };
  }, [operationId, onSnapshot, onDone]);

  if (hidden || !op) return null;

  const total = op.total_tasks || 0;
  const success = op.completed_tasks || 0;
  const failed = op.failed_tasks || 0;
  const done = success + failed;
  const failedState = hasFailures(op);
  const finished = isTerminal(op);

  return (
    <div className="py-2 first:pt-0 last:pb-0">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-0.5">
        <div className="text-sm font-medium text-[var(--ink-900)] truncate min-w-0 flex-1">
          {operationTitle(op)}
        </div>
        <div className="text-xs text-[var(--ink-500)] flex items-center gap-2 shrink-0">
          <span>{done} / {total}</span>
          <span className="text-[var(--success)]">✓ {success}</span>
          {failed > 0 && <span className="text-[var(--error)]">✕ {failed}</span>}
          {failedState && (
            <button
              type="button"
              onClick={() => onShowFailure(op)}
              className="underline text-[var(--error)] hover:opacity-80"
            >
              Details
            </button>
          )}
          <button
            type="button"
            onClick={() => onDone(operationId, op)}
            title="Ausblenden"
            aria-label={`${operationTitle(op)} ausblenden`}
            className="w-5 h-5 flex items-center justify-center bg-[var(--cream)] hover:bg-[var(--rule)] text-[var(--ink-500)] hover:text-[var(--ink-700)] leading-none transition-colors"
          >
            ✕
          </button>
        </div>
      </div>
      <SegmentedBar success={success} failed={failed} total={total} className="relative h-1.5 mt-1" />
      {!finished && op.current_task_text && (
        <div className="text-xs text-[var(--ink-700)] mt-1 break-words line-clamp-2" title={op.current_task_text}>
          {op.current_task_text}
        </div>
      )}
      {finished && (
        <div className={`text-xs mt-1 ${failedState ? 'text-[var(--error)]' : 'text-[var(--success)]'}`}>
          {failedState
            ? `${failed} Aufgabe${failed === 1 ? '' : 'n'} fehlgeschlagen`
            : 'Abgeschlossen'}
        </div>
      )}
    </div>
  );
};

/**
 * One client-driven operation (announced via `lro:local`). No polling — the
 * announcing component pushes fresh snapshots through App state. Mirrors the
 * OperationRow layout; a succeeded op lingers briefly, a failed one stays
 * until dismissed.
 */
const LocalOperationRow = ({ op, hidden, onDone }) => {
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    if (op.status !== 'succeeded') return undefined;
    const timer = setTimeout(() => onDoneRef.current(op.id), SUCCESS_LINGER_MS);
    return () => clearTimeout(timer);
  }, [op.status, op.id]);

  if (hidden) return null;

  const total = op.total || 0;
  const failedState = op.status === 'failed';
  const current = op.status === 'succeeded' ? total : Math.min(op.current || 0, total);
  const finished = isLocalTerminal(op);

  return (
    <div className="py-2 first:pt-0 last:pb-0">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-0.5">
        <div className="text-sm font-medium text-[var(--ink-900)] truncate min-w-0 flex-1">
          {op.title || 'Vorgang'}
        </div>
        <div className="text-xs text-[var(--ink-500)] flex items-center gap-2 shrink-0">
          <span>{current} / {total}</span>
          <button
            type="button"
            onClick={() => onDone(op.id)}
            title="Ausblenden"
            aria-label={`${op.title || 'Vorgang'} ausblenden`}
            className="w-5 h-5 flex items-center justify-center bg-[var(--cream)] hover:bg-[var(--rule)] text-[var(--ink-500)] hover:text-[var(--ink-700)] leading-none transition-colors"
          >
            ✕
          </button>
        </div>
      </div>
      <SegmentedBar
        success={current}
        failed={failedState ? Math.max(0, total - current) : 0}
        total={total}
        className="relative h-1.5 mt-1"
      />
      {!finished && op.text && (
        <div className="text-xs text-[var(--ink-700)] mt-1 break-words line-clamp-2" title={op.text}>
          {op.text}
        </div>
      )}
      {finished && (
        <div className={`text-xs mt-1 ${failedState ? 'text-[var(--error)]' : 'text-[var(--success)]'}`}>
          {failedState ? (op.text || 'Fehlgeschlagen') : 'Abgeschlossen'}
        </div>
      )}
    </div>
  );
};

const CHIP_SIZE = 44; // px — matches the chip's w-11/h-11
// Movement before a press becomes a drag, so taps still open the panel.
const DRAG_THRESHOLD = 6;
// A release after the pointer rested this long carries no fling velocity.
const FLING_STALE_MS = 80;
// Slight overshoot so the chip visibly "clicks" into its dock.
const SNAP_TRANSITION = 'transform 420ms cubic-bezier(0.34, 1.36, 0.64, 1)';

const clamp = (v, min, max) => Math.min(Math.max(v, min), Math.max(min, max));

const prefersReducedMotion = () => {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
};

/**
 * Drag/fling/snap state for the chip. The dock area is an invisible fixed
 * element (`.lro-dock-area`, insets in index.css) measured on mount and on
 * resize, so nav-bar and safe-area changes move the docks with them.
 */
const useDraggableDock = (active) => {
  const areaRef = useRef(null);
  const [area, setArea] = useState(null);
  const [dock, setDock] = useState(loadDock);
  // {x, y, target} while dragging; null when resting in a dock.
  const [drag, setDrag] = useState(null);
  // Snap animation is off for the first placement so the chip doesn't fly
  // in from the top-left corner.
  const [animate, setAnimate] = useState(false);
  const gesture = useRef(null);
  const suppressClick = useRef(false);

  useLayoutEffect(() => {
    const el = areaRef.current;
    if (!active || !el) return undefined;
    const measure = () => {
      const r = el.getBoundingClientRect();
      setArea({ left: r.left, top: r.top, right: r.right, bottom: r.bottom });
    };
    measure();
    window.addEventListener('resize', measure);
    let observer = null;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(measure);
      observer.observe(el);
    }
    return () => {
      window.removeEventListener('resize', measure);
      observer?.disconnect();
      setAnimate(false);
    };
  }, [active]);

  useEffect(() => {
    if (!area || animate) return undefined;
    const raf = requestAnimationFrame(() => setAnimate(true));
    return () => cancelAnimationFrame(raf);
  }, [area, animate]);

  const moveTo = useCallback((next) => {
    setDock(next);
    saveDock(next);
  }, []);

  const resting = area ? dockPosition(dock, area, CHIP_SIZE) : null;

  const onPointerDown = (e) => {
    suppressClick.current = false;
    if (!area || (e.pointerType === 'mouse' && e.button !== 0)) return;
    const origin = drag || resting;
    gesture.current = {
      id: e.pointerId,
      sx: e.clientX,
      sy: e.clientY,
      ox: origin.x,
      oy: origin.y,
      dragging: false,
      last: { x: origin.x, y: origin.y, t: e.timeStamp },
      v: { x: 0, y: 0 },
    };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e) => {
    const g = gesture.current;
    if (!g || g.id !== e.pointerId) return;
    const dx = e.clientX - g.sx;
    const dy = e.clientY - g.sy;
    if (!g.dragging) {
      if (Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
      g.dragging = true;
    }
    const x = clamp(g.ox + dx, 0, window.innerWidth - CHIP_SIZE);
    const y = clamp(g.oy + dy, 0, window.innerHeight - CHIP_SIZE);
    const dt = e.timeStamp - g.last.t;
    if (dt > 0) {
      // Smoothed velocity (px/ms) so one jittery sample can't misdirect a fling.
      g.v = {
        x: 0.7 * ((x - g.last.x) / dt) + 0.3 * g.v.x,
        y: 0.7 * ((y - g.last.y) / dt) + 0.3 * g.v.y,
      };
    }
    g.last = { x, y, t: e.timeStamp };
    setDrag({ x, y, target: nearestDock(projectFling({ x, y }, g.v), area, CHIP_SIZE) });
  };

  const finish = (e, withVelocity) => {
    const g = gesture.current;
    if (!g || g.id !== e.pointerId) return;
    gesture.current = null;
    if (!g.dragging) return;
    suppressClick.current = true;
    const fresh = withVelocity && e.timeStamp - g.last.t <= FLING_STALE_MS;
    const target = nearestDock(projectFling(g.last, fresh ? g.v : { x: 0, y: 0 }), area, CHIP_SIZE);
    moveTo(target);
    setDrag(null);
    try {
      navigator.vibrate?.(8);
    } catch {
      // Haptics are a nicety only.
    }
  };

  return {
    areaRef,
    area,
    dock,
    drag,
    position: drag || resting,
    transition: drag || !animate || prefersReducedMotion() ? 'none' : SNAP_TRANSITION,
    moveTo,
    // True once per completed drag: the click that follows the release
    // must not toggle the panel.
    consumeDragClick: () => {
      const was = suppressClick.current;
      suppressClick.current = false;
      return was;
    },
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: (e) => finish(e, true),
      onPointerCancel: (e) => finish(e, false),
    },
  };
};

// The panel opens toward the screen's interior from wherever the chip rests.
const panelPlacement = (dock) => {
  const { v, h } = dockParts(dock);
  const horizontal = h === 'left' ? 'left-0' : 'right-0';
  const vertical = v === 'top' ? 'top-full mt-2' : 'bottom-full mb-2';
  return { box: `${horizontal} ${vertical}`, listMaxH: v === 'middle' ? 'max-h-[35dvh]' : 'max-h-[50dvh]' };
};

/**
 * Global progress chip + on-demand panel for long-running operations.
 *
 * Default state is just a small square chip showing the aggregate
 * percentage and a hairline progress bar — it covers next to nothing. It
 * rests in one of six docks (corners and side midpoints of the
 * `.lro-dock-area` box, clear of the tab bar; bottom-right by default and
 * remembered per device). Dragging lifts it; on release it springs to the
 * dock nearest to where the gesture was heading, so a quick flick toward a
 * corner is enough. Tapping opens a panel (toward the screen's interior)
 * listing every operation; tapping again, Esc, or the panel's close control
 * folds it back.
 */
const OperationsTray = ({ operationIds, onOperationDone, localOps = [], onLocalOpDone }) => {
  const [expanded, setExpanded] = useState(false);
  const [snapshots, setSnapshots] = useState({});
  const [failureOp, setFailureOp] = useState(null);

  const onDoneRef = useRef(onOperationDone);
  onDoneRef.current = onOperationDone;

  const opCount = operationIds.length + localOps.length;
  const dockState = useDraggableDock(opCount > 0);

  const handleSnapshot = useCallback((id, op) => {
    setSnapshots((prev) => ({ ...prev, [id]: op }));
  }, []);

  const handleDone = useCallback((id) => {
    setSnapshots((prev) => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
    onDoneRef.current?.(id);
  }, []);

  const handleShowFailure = useCallback((op) => setFailureOp(op), []);

  const handleLocalDone = useCallback((id) => {
    onLocalOpDone?.(id);
  }, [onLocalOpDone]);

  if (!opCount) return null;

  const ops = operationIds.map((id) => snapshots[id]).filter(Boolean);
  const agg = ops.reduce(
    (acc, op) => ({
      total: acc.total + (op.total_tasks || 0),
      success: acc.success + (op.completed_tasks || 0),
      failed: acc.failed + (op.failed_tasks || 0),
    }),
    { total: 0, success: 0, failed: 0 }
  );
  localOps.forEach((op) => {
    const total = op.total || 0;
    const current = op.status === 'succeeded' ? total : Math.min(op.current || 0, total);
    agg.total += total;
    agg.success += current;
    if (op.status === 'failed') agg.failed += Math.max(0, total - current);
  });
  const anyFailed = ops.some(hasFailures) || localOps.some((op) => op.status === 'failed');
  const allFinished =
    ops.length === operationIds.length &&
    ops.every(isTerminal) &&
    localOps.every(isLocalTerminal);
  const pct = agg.total > 0
    ? Math.min(100, Math.round(((agg.success + agg.failed) / agg.total) * 100))
    : null;

  const { areaRef, area, dock, drag, position, transition, moveTo, consumeDragClick, handlers } = dockState;
  const dragging = !!drag;
  const placement = panelPlacement(dock);

  const handleChipClick = () => {
    if (consumeDragClick()) return;
    setExpanded((e) => !e);
  };

  const handleChipKeyDown = (e) => {
    if (e.key === 'Escape' && expanded) {
      setExpanded(false);
      return;
    }
    if (!e.key.startsWith('Arrow')) return;
    e.preventDefault();
    e.stopPropagation();
    moveTo(stepDock(dock, e.key));
  };

  return (
    <>
      <div ref={areaRef} className="lro-dock-area" aria-hidden="true" />

      {/* Snap targets, only while dragging; the one the chip will land in
          is highlighted. */}
      {dragging && area && DOCKS.map((d) => {
        const p = dockPosition(d, area, CHIP_SIZE);
        return (
          <div
            key={d}
            className="lro-snap-hint"
            data-active={drag.target === d}
            style={{ width: CHIP_SIZE, height: CHIP_SIZE, transform: `translate3d(${p.x}px, ${p.y}px, 0)` }}
          />
        );
      })}

      <div
        className="lro-chip-layer"
        data-dock={dock}
        style={{
          transform: position ? `translate3d(${position.x}px, ${position.y}px, 0)` : undefined,
          visibility: position ? 'visible' : 'hidden',
          transition,
        }}
      >
        {/* Detail panel — only when the user asks for it. Rows stay mounted
            while it is closed (they render null) so polling continues. */}
        <div
          className={
            expanded && !dragging
              ? `absolute ${placement.box} w-[min(20rem,calc(100vw-1.5rem))] bg-white border-2 border-[var(--ink-900)]`
              : 'hidden'
          }
        >
          <div className="flex items-center justify-between gap-3 px-3 py-2 border-b border-[var(--rule)]">
            <span className="text-sm font-medium text-[var(--ink-900)]">
              Vorgänge ({opCount})
            </span>
            <button
              type="button"
              onClick={() => setExpanded(false)}
              aria-label="Vorgänge einklappen"
              className="w-5 h-5 flex items-center justify-center bg-[var(--cream)] hover:bg-[var(--rule)] text-[var(--ink-500)] hover:text-[var(--ink-700)] leading-none transition-colors"
            >
              ✕
            </button>
          </div>
          <div className={`px-3 py-2 divide-y divide-[var(--rule)] ${placement.listMaxH} overflow-y-auto overscroll-contain`}>
            {operationIds.map((id) => (
              <OperationRow
                key={id}
                operationId={id}
                hidden={!expanded}
                onSnapshot={handleSnapshot}
                onDone={handleDone}
                onShowFailure={handleShowFailure}
              />
            ))}
            {localOps.map((op) => (
              <LocalOperationRow
                key={op.id}
                op={op}
                hidden={!expanded}
                onDone={handleLocalDone}
              />
            ))}
          </div>
        </div>

        {/* The chip: aggregate percentage + hairline progress bar. Drag to
            move; it snaps to the nearest dock on release. */}
        <button
          type="button"
          onClick={handleChipClick}
          onKeyDown={handleChipKeyDown}
          {...handlers}
          aria-expanded={expanded}
          aria-label={`Hintergrund-Vorgänge anzeigen (${opCount})`}
          aria-keyshortcuts="ArrowUp ArrowDown ArrowLeft ArrowRight"
          title="Hintergrund-Vorgänge"
          style={{ touchAction: 'none' }}
          className={`relative flex items-center justify-center w-11 h-11 bg-white border-2 overflow-hidden select-none transition-[transform,box-shadow,background-color] duration-150 ${
            dragging
              ? 'cursor-grabbing scale-110 shadow-[6px_6px_0_rgba(26,26,26,0.25)]'
              : 'cursor-grab shadow-none'
          } ${
            anyFailed
              ? 'border-[var(--error)] text-[var(--error)]'
              : 'border-[var(--ink-900)] text-[var(--ink-900)] hover:bg-[var(--cream)]'
          }`}
        >
          <span className="text-[11px] font-semibold tabular-nums leading-none">
            {anyFailed ? '!' : allFinished ? '✓' : pct !== null ? `${pct}%` : '…'}
          </span>
          <SegmentedBar
            success={agg.success}
            failed={agg.failed}
            total={agg.total}
            className="absolute bottom-0 inset-x-0 h-1"
          />
          {opCount > 1 && (
            <span className="absolute top-0 right-0 min-w-[14px] h-[14px] px-0.5 flex items-center justify-center bg-[var(--ink-900)] text-[var(--cream)] text-[9px] font-semibold leading-none">
              {opCount}
            </span>
          )}
        </button>
      </div>

      <OperationFailureDialog
        isOpen={!!failureOp}
        onClose={() => setFailureOp(null)}
        failedTaskNames={failureOp?.failed_task_names || []}
        operationName={operationTitle(failureOp)}
      />
    </>
  );
};

export default OperationsTray;
